import { z } from 'zod';
/*
 * SCO-593 — the return-to validator, browser side.
 *
 * ONE contract in three runtimes (the API's `src/lib/auth/returnPath.ts`, the
 * PHP login page's inline block, and this file); the three test matrices
 * assert the same returned values. A `redirect` value comes from the URL bar
 * and ends up as a navigation target, so only a same-origin path under one
 * of our route prefixes is admitted:
 *
 * - input is the already-extracted query value (never a whole URL);
 * - fragment dropped; pathname / query split at the first `?`;
 * - pathname: exactly one leading `/`; no backslash, control byte or `%` left
 *   after ONE decode (`%2f%2f`, `%5c`, `%0d%0a`, `%250d`, malformed `%zz` all
 *   fail); no empty or dot segment; the first segment is one of
 *   RETURN_PATH_PREFIXES matched whole; returned re-encoded per segment,
 *   without a trailing slash;
 * - query: preserved verbatim (never decoded), control bytes rejected;
 * - at most RETURN_PATH_MAX_BYTES UTF-8 bytes.
 *
 * On rejection the SINK'S OWN default comes back, never the supplied value.
 */
export var RETURN_PATH_MAX_BYTES = 1024;
export var RETURN_PATH_PREFIXES = ['admin', 'mobile', 'scoreboard', 'activate'];
var PREFIXES = new Set(RETURN_PATH_PREFIXES);
// The boundary check: a query value is external input (CLAUDE.md: Zod at API
// boundaries). Byte counting stays a plain loop — jsdom has no TextEncoder.
var rawSchema = z.string().min(1);
/** Where the producer app lands when it has nowhere better to go. */
export var MOBILE_HOME = '/mobile/authorized/games';
// C0 controls + DEL, literal.
// eslint-disable-next-line no-control-regex
var CONTROL = /[\x00-\x1f\x7f]/;
var utf8ByteLength = function (value) {
    var _a;
    var bytes = 0;
    // eslint-disable-next-line no-restricted-syntax
    for (var _i = 0, value_1 = value; _i < value_1.length; _i++) {
        var ch = value_1[_i];
        var cp = (_a = ch.codePointAt(0)) !== null && _a !== void 0 ? _a : 0;
        if (cp < 0x80)
            bytes += 1;
        else if (cp < 0x800)
            bytes += 2;
        else if (cp < 0x10000)
            bytes += 3;
        else
            bytes += 4;
    }
    return bytes;
};
var decodeOnce = function (value) {
    try {
        return decodeURIComponent(value);
    }
    catch (_a) {
        return null;
    }
};
export function safeReturnPath(input, fallback) {
    var _a;
    var parsed = rawSchema.safeParse(input);
    if (!parsed.success)
        return fallback;
    var raw = parsed.data;
    if (utf8ByteLength(raw) > RETURN_PATH_MAX_BYTES)
        return fallback;
    var withoutFragment = raw.split('#', 1)[0];
    var queryAt = withoutFragment.indexOf('?');
    var rawPath = queryAt === -1 ? withoutFragment : withoutFragment.slice(0, queryAt);
    var query = queryAt === -1 ? '' : withoutFragment.slice(queryAt + 1);
    if (!rawPath.startsWith('/'))
        return fallback;
    if (rawPath[1] === '/' || rawPath[1] === '\\')
        return fallback;
    if (CONTROL.test(query))
        return fallback;
    var path = decodeOnce(rawPath);
    if (path === null)
        return fallback;
    if (path.includes('\\') || path.includes('%') || CONTROL.test(path))
        return fallback;
    if (path === '/')
        return query ? "/?".concat(query) : '/';
    var segments = path.slice(1).replace(/\/$/, '').split('/');
    if (segments.some(function (s) { return s === '' || s === '.' || s === '..'; }))
        return fallback;
    if (!PREFIXES.has((_a = segments[0]) !== null && _a !== void 0 ? _a : ''))
        return fallback;
    var canonical = "/".concat(segments.map(encodeURIComponent).join('/'));
    var result = query ? "".concat(canonical, "?").concat(query) : canonical;
    // Re-encoding can grow a value up to 3x (non-ASCII segments); the cap holds
    // for the canonical value too, so what passes here passes again downstream.
    return utf8ByteLength(result) <= RETURN_PATH_MAX_BYTES ? result : fallback;
}
/** `to` with the destination carried as `?redirect=`, or `to` alone when there is none. */
export var withReturnTo = function (to, returnTo) { return (returnTo ? "".concat(to, "?redirect=").concat(encodeURIComponent(returnTo)) : to); };
var isMobilePath = function (path) { return path === '/mobile' || path.startsWith('/mobile/'); };
/**
 * After a successful code login in the mobile bundle: where a DOCUMENT
 * navigation must go, or null to stay in the producer app.
 *
 * - `mode=web` (new) or the legacy `redirect=web` flag: a website user signed
 *   in through the activation page — leave for the validated destination,
 *   `/` when there is none (the legacy behaviour).
 * - a validated destination outside the mobile bundle: leave for it.
 * - a destination inside the mobile bundle, none, or one the validator
 *   rejects: stay; the screens navigate() to the validated value or their
 *   own default themselves.
 */
export var webLoginDestination = function (search) {
    var params = new URLSearchParams(search);
    var webMode = params.get('mode') === 'web' || params.get('redirect') === 'web';
    var destination = safeReturnPath(params.get('redirect'), null);
    if (webMode)
        return destination !== null && destination !== void 0 ? destination : '/';
    return destination !== null && !isMobilePath(destination) ? destination : null;
};
/**
 * The login screens' destination for the current URL: a document navigation
 * out of the bundle when {@link webLoginDestination} says so, otherwise the
 * validated `redirect` (or the screen's own `fallback`) inside the router.
 */
export var loginDestinationFor = function (search, fallback) {
    var leaveFor = webLoginDestination(search);
    if (leaveFor !== null)
        return { to: leaveFor, leavesBundle: true };
    return { to: safeReturnPath(new URLSearchParams(search).get('redirect'), fallback), leavesBundle: false };
};
